import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import type { RootState } from 'app'
import { getAccountState } from 'modules/Account/store/selectors'
import Api, { paths } from 'shared/services/Api'
import {
  Accountant,
  BankAccount,
  LoadingStatus,
  Preferences,
  UserActions,
  UserCompany,
  UserDetails,
  UserSubscription,
} from 'shared/types'
import {
  getUserActions as getFetchedUserActions,
  getFetchUserConfigState,
  getCurrentSubscriptionState,
  getCompanyDataState,
} from './selectors'
import {
  GetCompanyDataResponse,
  EditCompanyPayload,
  CreateBankAccountPayload,
} from './actions.types'

const NAMESPACE = 'users'

export const setNoSubscription = createAction('setNoSubscription')
export const clearNotificationData = createAction(
  `${NAMESPACE}/clearNotificationData`
)

export const getUserDetails = createAsyncThunk<
  UserDetails,
  undefined,
  { rejectValue: string }
>(`${NAMESPACE}/getUserDetails`, async (_, thunkAPI) => {
  try {
    const response = await Api.get(paths.Users.dashboard)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getUserActions = createAsyncThunk<
  UserActions,
  { disableRefetch: boolean } | undefined,
  { rejectValue: string }
>(
  `${NAMESPACE}/getUserActions`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get(paths.Users.currentUser)
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = getFetchedUserActions(getState() as RootState)
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const assignAccountant = createAsyncThunk<
  Accountant,
  { emailAddress: string; language: string },
  { rejectValue: string }
>(`${NAMESPACE}/assignAccountant`, async (data, thunkAPI) => {
  try {
    const repsonse = await Api.post(paths.Users.accountant, data)
    return repsonse.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteAccountant = createAsyncThunk<
  void,
  undefined,
  { rejectValue: string }
>(`${NAMESPACE}/deleteAccountant`, async (_, thunkAPI) => {
  try {
    await Api.delete(paths.Users.accountant)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getAccountant = createAsyncThunk<
  Accountant,
  undefined,
  { rejectValue: string }
>(
  `${NAMESPACE}/getAccountant`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get(paths.Users.accountant)
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {
    condition: (_, { getState }) => {
      const { loading } = getAccountState(getState() as RootState)
      if (
        loading === LoadingStatus.Succeeded ||
        loading === LoadingStatus.Pending
      ) {
        return false
      }
    },
  }
)

export const getCompanyInfo = createAsyncThunk<
  GetCompanyDataResponse,
  { disableRefetch?: boolean } | undefined,
  { rejectValue: string }
>(
  `${NAMESPACE}/getCompanyInfo`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get(paths.Users.company)
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = getCompanyDataState(getState() as RootState)
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const editCompanyData = createAsyncThunk<
  void,
  EditCompanyPayload,
  { rejectValue: string }
>(`${NAMESPACE}/editCompanyData`, async (request, thunkAPI) => {
  try {
    const response = await Api.put(paths.Users.company, request)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const addUserConfig = createAsyncThunk<
  any,
  UserCompany,
  { rejectValue: { errorCode?: string } }
>(`${NAMESPACE}/addUserConfig`, async (data, thunkAPI) => {
  try {
    const response = await Api.post(paths.Users.config, data)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const editUserConfig = createAsyncThunk<
  any,
  UserCompany,
  { rejectValue: { errorCode?: string } }
>(`${NAMESPACE}/editUserConfig`, async (data, thunkAPI) => {
  try {
    const response = await Api.put(paths.Users.config, data)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getUserConfig = createAsyncThunk<
  UserCompany,
  { disableRefetch?: boolean } | undefined,
  { rejectValue: { errorCode?: string } }
>(
  `${NAMESPACE}/getUserConfig`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get<UserCompany>(paths.Users.config)
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = getFetchUserConfigState(getState() as RootState)
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const getCurrentSubscription = createAsyncThunk<
  UserSubscription,
  { disableRefetch?: boolean } | undefined,
  { rejectValue: { errorCode?: string } }
>(
  `${NAMESPACE}/getCurrentSubscription`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get<UserSubscription>(paths.Users.subscription)
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = getCurrentSubscriptionState(getState() as RootState)
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const getBankAccounts = createAsyncThunk<BankAccount[]>(
  `${NAMESPACE}/getBankAccounts`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get<BankAccount[]>(
        `${paths.BankAccounts.root}`
      )
      return response.data.sort((a, b) => a.id - b.id)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {}
)

export const createBankAccount = createAsyncThunk<
  BankAccount,
  CreateBankAccountPayload,
  {}
>(
  `${NAMESPACE}/createBankAccount`,
  async (payload, thunkAPI) => {
    try {
      const response = await Api.post<BankAccount>(
        `${paths.BankAccounts.root}`,
        payload
      )
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {}
)

export const updateBankAccount = createAsyncThunk<BankAccount, BankAccount>(
  `${NAMESPACE}/updateBankAccount`,
  async (payload, thunkAPI) => {
    try {
      const response = await Api.put(
        `${paths.BankAccounts.root}/${payload.id}`,
        payload
      )
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {}
)

export const deleteBankAccount = createAsyncThunk<void, number>(
  `${NAMESPACE}/deleteBankAccount`,
  async (payload, thunkAPI) => {
    try {
      await Api.delete(`${paths.BankAccounts.root}/${payload}`)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {}
)

export const getNotificationSettings = createAsyncThunk<Preferences>(
  `${NAMESPACE}/getNotificationSettings`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get<Preferences>(
        `${paths.Users.notificationSettings}`
      )
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {}
)

export const updateNotificationSettings = createAsyncThunk<
  Preferences,
  Preferences
>(
  `${NAMESPACE}/updateNotificationSettings`,
  async (payload, thunkAPI) => {
    try {
      const response = await Api.put(
        `${paths.Users.notificationSettings}`,
        payload
      )
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {}
)
